import { makeAutoObservable } from "mobx";
import dataRequest from "../http/DataRequest";
import {
  aggregateOlapRowsByDate,
  aggregateRowsByField,
  extractRestaurantGroups,
  parseOlapAxisLabels,
  safeNum,
} from "../utils/iikoOlapGroup";

class DatasStore {
  loading = false;
  error = null;
  sum = [];
  payTypesSum = [];
  payTypesName = [];
  categorySale = [];
  categoryName = [];
  fromDate = null;
  toDate = null;
  openDate = [];
  openDateIso = [];
  dishDiscountSumInt = [];
  dishDiscountSumIntAverage = [];
  guestNum = [];
  uniqOrderId = [];
  productCostBaseMarkUp = [];
  productCostBasePercent = [];
  averageByGuest = [];
  dishReturnSum = [];
  orderItems = [];
  groupByRestaurant = false;
  restaurantGroups = [];
  selectedRestaurantGroup = "";
  olapRowsRaw = [];
  payTypesRowsRaw = [];
  categoryRowsRaw = [];

  constructor() {
    makeAutoObservable(this);
  }

  setFromDate(date) {
    this.fromDate = date;
  }

  setToDate(date) {
    this.toDate = date;
  }

  setGroupByRestaurant(value) {
    const next = Boolean(value);
    if (this.groupByRestaurant === next) return;
    this.groupByRestaurant = next;
    if (!next) {
      this.selectedRestaurantGroup = "";
      this.restaurantGroups = [];
    }
  }

  setSelectedRestaurantGroup(name) {
    this.selectedRestaurantGroup = name || "";
  }

  applyOlapFromRaw() {
    let rows = Array.isArray(this.olapRowsRaw) ? [...this.olapRowsRaw] : [];

    if (this.groupByRestaurant && !this.selectedRestaurantGroup) {
      this.clearOlapSeries();
      return;
    }

    rows = aggregateOlapRowsByDate(rows);

    if (!rows.length) {
      this.clearOlapSeries();
      return;
    }

    const { openDate, openDateIso } = parseOlapAxisLabels(rows);
    this.openDate = openDate;
    this.openDateIso = openDateIso;
    this.dishDiscountSumInt = rows.map((el) => safeNum(el.DishDiscountSumInt));
    this.dishDiscountSumIntAverage = rows.map((el) => safeNum(el["DishDiscountSumInt.average"]));
    this.guestNum = rows.map((el) => safeNum(el.GuestNum));
    this.uniqOrderId = rows.map((el) => safeNum(el.UniqOrderId));
    this.productCostBaseMarkUp = rows
      .map((el) => safeNum(el["ProductCostBase.MarkUp"]) * 100)
      .map((n) => n.toFixed(2));
    this.productCostBasePercent = rows
      .map((el) => safeNum(el["ProductCostBase.Percent"]) * 100)
      .map((n) => n.toFixed(2));
    this.averageByGuest = rows.map((el) => safeNum(el["DishDiscountSumInt.averageByGuest"]));
    this.dishReturnSum = rows.map((el) => safeNum(el.DishReturnSum));
    this.orderItems = rows.map((el) => safeNum(el.OrderItems));
    this.sum = [...this.dishDiscountSumInt];
  }

  clearOlapSeries() {
    this.openDate = [];
    this.openDateIso = [];
    this.dishDiscountSumInt = [];
    this.dishDiscountSumIntAverage = [];
    this.guestNum = [];
    this.uniqOrderId = [];
    this.productCostBaseMarkUp = [];
    this.productCostBasePercent = [];
    this.averageByGuest = [];
    this.dishReturnSum = [];
    this.orderItems = [];
    this.sum = [];
  }

  applyPayCategoryFromRaw() {
    let payRows = Array.isArray(this.payTypesRowsRaw) ? this.payTypesRowsRaw : [];
    let catRows = Array.isArray(this.categoryRowsRaw) ? this.categoryRowsRaw : [];

    const payAgg = aggregateRowsByField(payRows, "PayTypes");
    this.payTypesSum = payAgg.sums;
    this.payTypesName = payAgg.names;

    const catAgg = aggregateRowsByField(catRows, "DishCategory");
    this.categoryName = catAgg.names;
    this.categorySale = catAgg.sums;

    if (this.categoryName.length > 0) {
      this.categoryName = this.categoryName.map((name, idx) =>
        idx === 0 && (name == null || name === "" || name === "—") ? "Без категории" : name,
      );
    }
  }

  clearError() {
    this.error = null;
  }

  async fetchRestaurantGroups(id, dateFrom, dateTo) {
    const groupsResp = await dataRequest.getIikoOlapData(id, dateFrom, dateTo, true, null);
    const groups = extractRestaurantGroups(groupsResp?.data || []);
    this.restaurantGroups = groups;
    if (!groups.length) {
      this.selectedRestaurantGroup = "";
      return false;
    }
    if (!groups.includes(this.selectedRestaurantGroup)) {
      this.selectedRestaurantGroup = groups[0];
    }
    return true;
  }

  async loadPeriodData(id, dateFrom, dateTo) {
    if (id === "" || id == null) return;
    this.loading = true;
    this.error = null;
    try {
      if (this.groupByRestaurant) {
        const hasGroups = await this.fetchRestaurantGroups(id, dateFrom, dateTo);
        if (!hasGroups || !this.selectedRestaurantGroup) {
          this.olapRowsRaw = [];
          this.payTypesRowsRaw = [];
          this.categoryRowsRaw = [];
          this.clearOlapSeries();
          this.applyPayCategoryFromRaw();
          this.error = null;
          return;
        }
      }

      const group = this.groupByRestaurant ? this.selectedRestaurantGroup : null;
      // Sequential, not Promise.all — iiko's API guidance asks for one request
      // at a time against a given login (see helpers/dateRange.js on the
      // backend); now that this path also serves year-long ranges, running
      // these concurrently would double the in-flight load at the worst time.
      const olapResponse = await dataRequest.getIikoOlapData(id, dateFrom, dateTo, this.groupByRestaurant, group);
      const docResponse = await dataRequest.getIikoData(id, dateFrom, dateTo, this.groupByRestaurant, group);

      this.olapRowsRaw = Array.isArray(olapResponse?.data) ? olapResponse.data : [];

      this.payTypesRowsRaw = Array.isArray(docResponse?.payTypes?.data) ? docResponse.payTypes.data : [];
      this.categoryRowsRaw = Array.isArray(docResponse?.categorySales?.data)
        ? docResponse.categorySales.data
        : [];

      this.applyOlapFromRaw();
      this.applyPayCategoryFromRaw();
      this.error = null;
    } catch (error) {
      this.error = error?.message || "Ошибка загрузки данных";
      this.olapRowsRaw = [];
      this.clearOlapSeries();
    } finally {
      this.loading = false;
    }
  }

  async olapDatas(id, dateFrom, dateTo) {
    await this.loadPeriodData(id, dateFrom, dateTo);
  }

  fetchDocument = async (id, dateFrom, dateTo) => {
    await this.loadPeriodData(id, dateFrom, dateTo);
  };
}

const datasStore = new DatasStore();

export default datasStore;
